// Templates 01–03 · Booking · organizer
// Sent to whoever paid for the round.

import {
  renderShell,
  renderStatusDot,
  renderHeading,
  renderParagraph,
  renderInfoCard,
  renderButtonRow,
  renderFooter,
  renderCallout,
  tokens,
  cleanSubject,
} from './kit.ts';
import {
  formatLongDate,
  formatTimeRange,
  formatCurrency,
  shortBookingId,
  formatTime,
  escapeHtml,
} from './format.ts';
import type {
  RenderedEmail,
  BookingConfirmationData,
  AutoCancellationData,
  PostEventRecapData,
} from './types.ts';

const monoStack = `ui-monospace, 'SF Mono', 'Roboto Mono', Menlo, monospace`;
const fontStack = `'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif`;

// ─── 01 · BOOKING CONFIRMATION ────────────────────────────────────────────
export function renderBookingConfirmation(d: BookingConfirmationData): RenderedEmail {
  const dateLong = formatLongDate(d.date);
  const timeRange = formatTimeRange(d.startTime, d.endTime);
  const subject = cleanSubject(`You're on the books at ${d.vendorName} · ${dateLong}`);
  const preheader = `Receipt, calendar invite, and the manage-booking link inside.`;

  const body = `
    ${renderStatusDot('Confirmed', 'confirmed')}
    ${renderHeading("Locked in. The tab's", 'on us', 'to handle.')}
    ${renderParagraph(
      `Hey ${escapeHtml(d.organizerFirstName)} — your team's round is booked. We've held the slot and pre-authed your card; we'll charge it at 3 PM on the day, after the final headcount lock.`,
    )}
    ${renderInfoCard(
      [
        { label: 'Bar', value: `${escapeHtml(d.vendorName)}${d.vendorAddress ? ` · ${escapeHtml(d.vendorAddress)}` : ''}` },
        { label: 'When', value: `${escapeHtml(dateLong)} · ${escapeHtml(timeRange)}` },
        { label: 'Package', value: `${escapeHtml(d.offerTitle)} · ${formatCurrency(d.pricePerHeadCents)} / head` },
        { label: 'Capacity', value: `Up to ${d.capacityMax ?? d.headcount}${d.capacityMin ? ` · ${d.capacityMin} minimum` : ''}` },
        { label: 'Total hold', value: `${formatCurrency(d.totalCents)} <span style="color:${tokens.inkSoft};">(pre-auth, charged at 3 PM)</span>` },
      ],
      { tone: 'amber', image: d.heroImage },
    )}
    ${renderButtonRow([
      { label: 'Manage booking →', href: d.manageUrl, style: 'primary' },
      { label: 'Add to calendar', href: d.calendarUrl, style: 'ghost' },
    ])}
    ${renderParagraph(`Next up: invite your team. We'll DM Slack and send RSVPs, then nudge anyone who ghosts.`, { muted: true })}
  `;

  const footer = renderFooter({ tagline: `Goaround Inc · 81 Prospect St, Brooklyn NY`, pill: 'Transactional', unsubscribeUrl: `${d.siteUrl}/unsubscribe` });

  return {
    subject,
    preheader,
    html: renderShell({
      preheader,
      headerLabel: `Booking #${shortBookingId(d.bookingId)}`,
      body,
      footer,
    }),
  };
}

// ─── 02 · AUTO-CANCELLATION NOTICE ────────────────────────────────────────
export function renderAutoCancellation(d: AutoCancellationData): RenderedEmail {
  const dateLong = formatLongDate(d.date);
  const dayName = dateLong.split(',')[0];
  const subject = cleanSubject(`We cancelled ${dayName}'s round at ${d.vendorName}`);
  const preheader = `Refund issued. Reschedule in two clicks.`;

  const body = `
    ${renderStatusDot('Cancelled · Refunded', 'cancelled')}
    ${renderHeading(`${dayName} came up`, 'short.')}
    ${renderParagraph(
      `Only ${d.rsvpYes} of your team had RSVP'd yes by the 24-hour mark — below ${escapeHtml(d.vendorName)}'s ${d.capacityMin ?? 4}-person minimum — so we cancelled the booking and refunded your card in full.`,
    )}
    ${renderInfoCard(
      [
        { label: 'Was', value: `${escapeHtml(dateLong)} · ${formatTimeRange(d.startTime, d.endTime)} · ${escapeHtml(d.vendorName)}` },
        { label: "RSVP'd", value: `${d.rsvpYes} yes · ${d.rsvpMaybe} maybe · ${d.rsvpNone} no response` },
        { label: 'Refunded', value: `${formatCurrency(d.refundCents)}${d.cardLast4 ? ` to Visa ••${escapeHtml(d.cardLast4)}` : ''} <span style="color:${tokens.inkSoft};">(1–3 business days)</span>` },
      ],
      { tone: 'pink' },
    )}
    ${d.callout ? renderCallout(d.callout) : ''}
    ${renderButtonRow([
      { label: 'Reschedule in 2 clicks →', href: d.rescheduleUrl, style: 'pop' },
      { label: 'Browse other bars', href: d.browseUrl, style: 'ghost' },
    ])}
  `;

  const footer = renderFooter({ tagline: `Booking cancelled · Help`, pill: 'Cancellation' });

  return {
    subject,
    preheader,
    html: renderShell({
      preheader,
      headerLabel: `Booking #${shortBookingId(d.bookingId)}`,
      body,
      footer,
    }),
  };
}

// ─── 03 · POST-EVENT RECAP + REVIEW ───────────────────────────────────────
export function renderPostEventRecap(d: PostEventRecapData): RenderedEmail {
  const subject = cleanSubject(`How was ${d.vendorName} last night?`);
  const preheader = `90 seconds to rate, and you're done.`;

  // 5 star buttons that route to the review page with the rating prefilled.
  const stars = [1, 2, 3, 4, 5]
    .map(
      (n) =>
        `<a href="${escapeHtml(d.reviewUrl)}?rating=${n}" style="display:inline-block;color:${tokens.amber};font-size:32px;line-height:1;text-decoration:none;padding:0 4px;">★</a>`,
    )
    .join('');

  const body = `
    ${renderStatusDot('After party', 'action')}
    ${renderHeading(`How'd ${d.vendorName}`, 'treat', 'the team?')}
    ${renderParagraph(
      `${d.arrived} of ${d.invited} of your folks made it. We charged ${formatCurrency(d.chargedCents)} (${d.invited - d.arrived} no-show${d.invited - d.arrived === 1 ? '' : 's'} refunded automatically per our headcount window). Quick rate?`,
    )}
    <div style="text-align:center;margin:8px 0 12px;">${stars}</div>
    <div style="text-align:center;font-family:${monoStack};font-size:11px;letter-spacing:0.16em;text-transform:uppercase;color:${tokens.inkSoft};margin-bottom:24px;">Tap to rate · public on ${escapeHtml(d.vendorName)}'s bar page</div>
    <table cellpadding="0" cellspacing="0" border="0" width="100%" role="presentation" style="border-collapse:collapse;margin:0 0 18px;"><tr>
      <td style="border-bottom:1px solid ${tokens.line};height:1px;"></td>
      <td width="40%" align="center" style="font-family:${monoStack};font-size:10px;letter-spacing:0.16em;text-transform:uppercase;color:${tokens.inkSoft};padding:0 10px;">Or leave a quick note</td>
      <td style="border-bottom:1px solid ${tokens.line};height:1px;"></td>
    </tr></table>
    <div style="border:1px solid ${tokens.line};border-radius:10px;padding:14px 16px;color:${tokens.inkSoft};font-family:${fontStack};font-size:14px;font-style:italic;margin:0 0 24px;">"The bartender remembered our names by round two…"</div>
    ${renderButtonRow([
      { label: 'Leave a review', href: d.reviewUrl, style: 'primary' },
      { label: 'Book again', href: d.bookAgainUrl, style: 'ghost' },
    ])}
  `;

  const footer = renderFooter({ tagline: `One-time recap · won't ask twice`, pill: 'Engagement' });

  return {
    subject,
    preheader,
    html: renderShell({ preheader, headerLabel: 'Recap', body, footer }),
  };
}
